import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { SortTool } from '../../common/react/components/SortTool';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { QueryString } from '../../common/react/QueryString';
import { SorterActions } from '../../common/react/SorterActions';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { ContentSortOptions } from '../../ts/models/ContentSortOptions';
import { WorkbookContentType } from '../../ts/models/ContentTypes';
import { PlaceName } from '../../ts/models/IPlace';
import { IWorkbookDetailInfo, IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { OrderKeys } from '../../ts/models/OrderKeys';
import { IActionButton } from '../../ts/services/ActionPanelService';

export module WorkbookGrid {

  export type WorkbookGridScope =
      CommonDataGridColumns.ISelectColumnScope<IWorkbookInfo> &
      CommonDataGridColumns.IFavoriteColumnScope<IWorkbookInfo>;

  export function columns(
    // Dependencies
    translate: IReactTranslateService,
    scope: WorkbookGridScope,
    angularContext: AngularContext,

    // Other params
    siteUrls: SiteUrls,
    actions: IActionButton<IWorkbookInfo, IWorkbookDetailInfo>[],
    getActionTarget: () => IActionTarget<IWorkbookInfo, IWorkbookDetailInfo>,
    anyActionsAllowed: boolean,
    inProjectPlace: boolean,
    sorterActions: SorterActions,
    queryString: QueryString)
  : DataGrid.Column[] {

    const nameColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace({
      name: 'name-col',
      dataKey: 'name',
      headerText: translate('column_name'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        return {
          text: row.data.name,
          url: siteUrls.workbookUrl(row.data.id),
          iconClassName: 'tb-grid-icon-workbook',
          iconTitle: translate('workbook')
        };
      })
    });

    const nameColumnWithActions = CommonDataGridColumns.withActions(nameColumn, {
      actions: actions,
      getActionTarget: getActionTarget,
      translate: translate
    }, scope);

    const hitsColumn = CommonDataGridColumns.selectableColumnWithBars('hitsCol',
      ['hitsTotal', 'hitsLastTwelveMonthsTotal', 'hitsLastThreeMonthsTotal', 'hitsLastOneMonthTotal'],
      queryString, translate, sorterActions, 'hitsTotal');

    const numSheetsColumn: DataGrid.Column = {
      name: 'num-sheets-col',
      dataKey: 'numSheets',
      headerText: translate('column_numSheets'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('numSheets')),
      direction: 'desc',
    };

    const sizeColumn: DataGrid.Column = {
      name: 'size-col',
      dataKey: 'size',
      headerText: translate('column_size'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.SizeCell('size')),
      direction: 'desc',
    };

    const projectNameColumn: DataGrid.Column = {
        name: 'project-name-col',
        dataKey: 'projectName',
        headerText: translate('column_project'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
          return {
            text: row.data.project.name,
            url: siteUrls.projectUrl(row.data.project.id)
          };
        })
      };

    const updatedAtColumn: DataGrid.Column = {
      name: 'updated-at-col',
      dataKey: 'updatedAt',
      headerText: translate('column_updated'),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('updatedAt')
    };

    return _.compact([
      anyActionsAllowed && CommonDataGridColumns.selected(scope),
      CommonDataGridColumns.favorite(scope),
      anyActionsAllowed ? nameColumnWithActions : nameColumn,
      hitsColumn,
      numSheetsColumn,
      sizeColumn,
      !inProjectPlace && projectNameColumn,
      CommonDataGridColumns.owner(translate, siteUrls),
      updatedAtColumn,
      CommonDataGridColumns.alert(WorkbookContentType.name, translate, angularContext)
    ]);
  }

  export function sortOptions(place: PlaceName, hasSearchFilter: boolean): SortTool.SortOption[] {
    return ContentSortOptions.sortOptionsForValidKeys([
      hasSearchFilter && OrderKeys.relevance,
      OrderKeys.name,
      OrderKeys.hitsTotal,
      OrderKeys.hitsLastTwelveMonthsTotal,
      OrderKeys.hitsLastThreeMonthsTotal,
      OrderKeys.hitsLastOneMonthTotal,
      OrderKeys.numSheets,
      OrderKeys.size,
      place !== 'project' && OrderKeys.projectName,
      OrderKeys.ownerName,
      OrderKeys.updatedAt,
      OrderKeys.hasAlert
    ]);
  }
}
